import { isMovieMediaType, movieHasDisplayableCuratorScore, movieHasUserRatingSources, ratingsFromItem } from "./appleTvCuratorScoreEligibility";

const SERVICE_PRIORITY = [
  "Netflix",
  "Apple TV Plus",
  "Amazon Prime Video",
  "Prime Video",
  "Disney Plus",
  "Hulu",
  "Max",
  "HBO Max",
  "Peacock",
  "Paramount Plus",
];

const DISPLAY_EXCLUDED = [
  /\byoutube\s*tv\b/i,
  /\bchannel\b/i,
  /\bamazon channel\b/i,
  /\broku premium channel\b/i,
];

/** Purchase / rent storefronts — collapse to "Buy/rent" in How to Watch. */
const BUY_RENT_PROVIDER_PATTERNS = [
  /\bamazon\s*video\b/i,
  /\bapple\s*tv\s*store\b/i,
  /\bitunes\b/i,
  /\bgoogle\s*play\b/i,
  /\byoutube\b/i,
  /\bfandango\b/i,
  /\bvudu\b/i,
  /\bmicrosoft\s*store\b/i,
  /\bxbox\b/i,
  /\bplaystation\b/i,
  /\bdirectv\b/i,
  /\bspectrum\b/i,
  /\bverizon\b/i,
  /\bflixtor\b/i,
  /\brach\s*tv\b/i,
  /\bsky\s*store\b/i,
];

export function isBuyRentWatchProvider(name) {
  const value = String(name || "").trim();
  if (!value) return false;
  // Keep subscription streaming even when the label contains overlapping tokens
  // (e.g. "Amazon Prime Video", "YouTube TV" already excluded above).
  if (/\bprime\s*video\b/i.test(value) || /\bamazon\s*prime\b/i.test(value)) return false;
  if (/\bapple\s*tv\s*(\+|plus)\b/i.test(value)) return false;
  return BUY_RENT_PROVIDER_PATTERNS.some((pattern) => pattern.test(value));
}

function normalizeDisplayProvider(name) {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "";
  if (/^netflix\b/i.test(trimmed)) return "Netflix";
  // Bare "Apple TV" from TMDB flatrate is Apple TV+; storefront stays buy/rent.
  if (/^apple tv$/i.test(trimmed)) return "Apple TV Plus";
  if (/^(hbo\s*)?max$/i.test(trimmed)) return "Max";
  return trimmed;
}

export function filterDisplayWatchSources(watchSources) {
  if (!Array.isArray(watchSources)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of watchSources) {
    const trimmed = String(raw || "").trim();
    if (!trimmed) continue;
    if (DISPLAY_EXCLUDED.some((pattern) => pattern.test(trimmed))) continue;
    if (isBuyRentWatchProvider(trimmed)) continue;
    const label = normalizeDisplayProvider(trimmed);
    const key = label.toLowerCase();
    if (!label || seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

/** True when sources include purchase/rent storefronts (even if streaming is absent). */
export function hasBuyRentWatchSources(watchSources) {
  if (!Array.isArray(watchSources)) return false;
  return watchSources.some((raw) => {
    const trimmed = String(raw || "").trim();
    if (!trimmed) return false;
    if (DISPLAY_EXCLUDED.some((pattern) => pattern.test(trimmed))) return false;
    return isBuyRentWatchProvider(trimmed);
  });
}

// TMDB watch-provider logos. logo_path values come straight from
// /3/watch/providers/movie. Served from the TMDB image CDN as branded,
// full-color square logos. Ordered most-specific first so that, e.g.,
// "Apple TV Plus" matches before the bare "Apple TV" store entry and
// "Amazon Prime Video" matches before "Amazon Video".
const TMDB_IMAGE_BASE = "https://image.tmdb.org/t/p/w154";

const PROVIDER_LOGOS = [
  { match: /apple\s*tv\+|apple\s*tv\s*plus/i, path: "/6uhKBfmtzFqOcLousHwZuzcrScK.jpg" },
  { match: /apple\s*tv/i, path: "/peURlLlr8jggOwK53fJ5wdQl05y.jpg" },
  { match: /netflix/i, path: "/t2yyOv40HZeVlLjYsCsPHnWLk4W.jpg" },
  { match: /amazon\s*prime|prime\s*video/i, path: "/pvske1MyAoymrs5bguRfVqYiM9a.jpg" },
  { match: /amazon\s*video/i, path: "/5NyLm42TmCqCMOZFvH4fcoSNKEW.jpg" },
  { match: /disney/i, path: "/7rwgEs15tFwyR9NPQ5vpzxTj19Q.jpg" },
  { match: /hulu/i, path: "/zxrVdFjIjLqkfnwyghnfywTn3Lh.jpg" },
  { match: /hbo\s*max|^max\b|\bmax\b/i, url: "/assets/hbo-max.png" },
  { match: /\bhbo\b/i, path: "/fWqVPYArdFwBc6vYqoyQB6XUl85.jpg" },
  { match: /peacock/i, path: "/8VCV78prwd9QzZnEm0ReO6bERDa.jpg" },
  { match: /paramount/i, path: "/xbhHHa1YgtpwhC8lb1NQ3ACVcLd.jpg" },
  { match: /\bstarz\b/i, path: "/eWp5LdR4p4uKL0wACBBXapDV2lB.jpg" },
  { match: /showtime/i, path: "/4kL33LoKd99YFIaSOoOPMQOSw1A.jpg" },
  { match: /mgm/i, path: "/6A1gRIJqLfFHOoTvbTxDAbuU2nQ.jpg" },
  { match: /amc/i, path: "/xlonQMSmhtA2HHwK3JKF9ghx7M8.jpg" },
  { match: /tubi/i, path: "/zLYr7OPvpskMA4S79E3vlCi71iC.jpg" },
  { match: /pluto/i, path: "/t6N57S17sdXRXmZDAkaGP0NHNG0.jpg" },
  { match: /crunchyroll/i, path: "/8Gt1iClBlzTeQs8WQm8UrCoIxnQ.jpg" },
  { match: /fubo/i, path: "/9BgaNQRMDvVlji1JBZi6tcfxpKx.jpg" },
  { match: /philo/i, path: "/ptmbGSttkyzawLbxx9MElmxKuVo.jpg" },
  { match: /criterion/i, path: "/4TJTNWd2TT1kYj6ocUEsQc8WRgr.jpg" },
  { match: /mubi/i, path: "/bVR4Z1LCHY7gidXAJF5pMa4QrDS.jpg" },
  { match: /kanopy/i, path: "/rcBwnERpNfPfWB5DaSTyEMCZbCA.jpg" },
  { match: /hoopla/i, path: "/j7D006Uy3UWwZ6G0xH6BMgIWTzH.jpg" },
  { match: /shudder/i, path: "/pheENW1BxlexXX1CKJ4GyWudyMA.jpg" },
  { match: /britbox/i, path: "/aGIS8maihUm60A3moKYD9gfYHYT.jpg" },
  { match: /sundance/i, path: "/pZ9TSk3wlRYwiwwRxTsQJ7t2but.jpg" },
  { match: /crackle/i, path: "/7P2JHkfv4AmU2MgSPGaJ0z6nNLG.jpg" },
  { match: /hallmark/i, path: "/llEJ6av9kAniTQUR9hF9mhVbzlB.jpg" },
  { match: /hi-?yah/i, path: "/mB2eDIncwSAlyl8WAtfV24qEIkk.jpg" },
  { match: /the\s*roku\s*channel|\broku\b/i, path: "/z0h7mBHwm5KfMB2MKeoQDD2ngEZ.jpg" },
  { match: /freevee/i, path: "/uBE4RMH15mrkuz6vXzuJc7ZLXp1.jpg" },
  { match: /\bplex\b/i, path: "/wDWvnupneMbY6RhBTHQC9zU0SCX.jpg" },
  { match: /directv/i, path: "/xL9SUR63qrEjFZAhtsipskeAMR7.jpg" },
  { match: /google\s*play/i, path: "/tbEdFQDwx5LEVr8WpSeXQSIirVq.jpg" },
  { match: /\bvudu\b|fandango/i, path: "/21dEscfO8n1tL35k4DANixhffsR.jpg" },
  { match: /microsoft\s*store/i, path: "/shq88b09gTBYC4hA7K7MUL8Q4zP.jpg" },
  { match: /youtube/i, path: "/oIkQkEkwfmcG7IGpRR1NB8frZZM.jpg" },
];

function firstWord(value) {
  const cleaned = String(value || "")
    .replace(/[^\w\s]/g, " ")
    .trim();
  if (!cleaned) return "Curator";
  return cleaned.split(/\s+/)[0];
}

export function hasValidCuratorScore(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

export function isTvMediaType(item) {
  const mt = String(item?.mediaType || item?.media_type || "").toLowerCase();
  return mt === "tv" || mt === "tv-show" || mt === "tv_show" || mt === "series";
}

function formatCompactCount(n) {
  if (n >= 1_000_000) {
    return `${Math.round(n / 1_000_000)}M`;
  }
  if (n >= 1000) {
    return `${Math.round(n / 1000)}K`;
  }
  return String(Math.round(n));
}

export function formatRatingsCount(value, options = {}) {
  const { compact = false } = options;
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (compact || n >= 1000) return formatCompactCount(n);
  return String(Math.round(n));
}

// 2025 U.S. average movie ticket price (USD). TMDB revenue is worldwide, so
// dividing by a U.S. ticket price yields a conservative admissions proxy.
const AVG_THEATER_TICKET_USD = 16.08;

/** Rough theatrical-admissions estimate from worldwide box office revenue. */
export function estimateTheaterAdmissions(revenue) {
  const n = Number(revenue);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n / AVG_THEATER_TICKET_USD);
}

export function resolveTheaterViewershipCount(item) {
  const revenue = Number(item?.revenue ?? item?.metadata?.revenue ?? 0);
  return estimateTheaterAdmissions(revenue);
}

export const MIN_POSTER_SCORE_RATINGS = 10000;

function resolveRatingsCount(item) {
  const count = Number(item?.ratingsCount ?? item?.vote_count ?? 0);
  return Number.isFinite(count) ? count : 0;
}

export function hasEnoughRatingsForScore(item, minimum = MIN_POSTER_SCORE_RATINGS) {
  return resolveRatingsCount(item) >= minimum;
}

/** Curator shelf/hero score — prefer catalog percentile, fall back to raw score. */
// Score used for shelf gating, sorting, and hero eligibility. Prefers the
// Curator percentile, falling back to the raw score so shelf composition and
// ranking stay stable even for titles whose percentile isn't computed yet.
export function resolveCuratorDisplayScore(item) {
  const percentile = item?.curator_metrics?.score_percentile
    ?? item?.curator_score_percentile
    ?? item?.scoreBreakdown?.curator?.percentile
    ?? item?.scoreBreakdown?.curator?.score_percentile
    ?? null;
  const rawScore = item?.curator_metrics?.score
    ?? item?.curator_score
    ?? item?.scoreBreakdown?.curator?.score
    ?? item?.score
    ?? null;
  const value = percentile ?? rawScore;
  if (!hasValidCuratorScore(value)) return null;
  if (isMovieMediaType(item) && !movieHasDisplayableCuratorScore(item)) return null;
  return Number(value);
}

/** Curator *percentile* only — used for the visible score badge on browse
 * surfaces so we never render a raw score as if it were a percentile. Returns
 * null when no percentile has been computed (caller shows no Curator badge). */
export function resolveCuratorPercentileDisplay(item) {
  const percentile = resolveCuratorPercentileValue(item);
  if (percentile == null) return null;
  if (isMovieMediaType(item) && !movieHasDisplayableCuratorScore(item)) return null;
  return percentile;
}

/** Catalog percentile with no eligibility / ratings gates (curated shelves). */
export function resolveCuratorPercentileValue(item) {
  const percentile = item?.curator_metrics?.score_percentile
    ?? item?.curator_score_percentile
    ?? item?.scoreBreakdown?.curator?.percentile
    ?? item?.scoreBreakdown?.curator?.score_percentile
    ?? null;
  if (!hasValidCuratorScore(percentile)) return null;
  return Number(percentile);
}

/** Raw curator blend 1–100 (not catalog percentile). */
export function resolveCuratorRawScore(item) {
  const rawScore = item?.curator_metrics?.score
    ?? item?.curator_score
    ?? item?.scoreBreakdown?.curator?.score
    ?? null;
  // Avoid treating a percentile already stored in item.score as the raw blend.
  if (!hasValidCuratorScore(rawScore)) return null;
  return Number(rawScore);
}

/**
 * Visible poster/overlay score badge:
 * - Movies → catalog percentile
 * - TV → raw curator blend 1–100 (never catalog percentile)
 */
export function resolvePosterOverlayScore(item) {
  if (isTvMediaType(item)) {
    const raw = resolveCuratorRawScore(item);
    if (raw != null) return raw;
    // TMDB-only TV titles: show vote_average on a 0–100 scale.
    const fromTmdbOnly = item?.fromTmdbSearch === true || item?.inCuratorCatalog === false;
    const tmdb = Number(item?.tmdbScore ?? item?.vote_average);
    if (fromTmdbOnly && Number.isFinite(tmdb) && tmdb > 0) {
      return tmdb <= 10 ? Math.round(tmdb * 10) : Math.round(tmdb);
    }
    return null;
  }
  return resolveCuratorPercentileDisplay(item);
}

/**
 * Map a source's raw score onto a 0–100 battery fill.
 * IMDb / Metacritic users: /10 → ×10. Letterboxd: /5 → ×20.
 * Metascore, RT Tomatometer, Curator raw: already 0–100.
 */
export function rawSourceScoreToFillPercent(sourceKey, rawScore) {
  const n = Number(rawScore);
  if (!Number.isFinite(n) || n < 0) return null;
  const key = String(sourceKey || "").toLowerCase();
  let fill;
  if (key === "imdb" || key === "metacritic-user" || key === "metacritic_user") {
    fill = n <= 10 ? n * 10 : n;
  } else if (key === "letterboxd" || key === "lb") {
    fill = n <= 5 ? n * 20 : n;
  } else {
    // curator / metacritic / rt / already-normalized
    fill = n;
  }
  return Math.round(Math.max(0, Math.min(100, fill)));
}

/**
 * Current Letterboxd weekly chart rank only. `popularityRank` falls back to
 * month / Netflix / IMDb ranks, which are not current weekly chart status.
 */
export function resolveLetterboxdWeekRank(item) {
  const metrics = item?.popularity_metrics || item?.popularity || {};
  const rank = Number(item?.letterboxdWeekRank ?? metrics?.letterboxd_week_rank);
  return Number.isFinite(rank) && rank > 0 ? rank : null;
}

/** Popular movies follow the current weekly chart; other rows retain the score floor. */
export function meetsPopularShelfScore(item, minScore = 50) {
  if (item?.fromTmdbSearch === true || item?.inCuratorCatalog === false) return false;
  if (isMovieMediaType(item) && resolveLetterboxdWeekRank(item) != null) {
    return Boolean(item?.poster);
  }
  if (!hasEnoughRatingsForScore(item)) return false;
  if (isMovieMediaType(item) && !movieHasDisplayableCuratorScore(item)) return false;
  const score = resolveCuratorDisplayScore(item);
  if (score == null || score < minScore) return false;
  return Boolean(formatPosterScoreLine(item));
}

/** Curator score for list sorting — null when the title is not curator-score eligible. */
export function resolveListSortScore(item) {
  return resolveCuratorDisplayScore(item);
}

export function compareListSortScores(a, b, sortOrder = "desc") {
  const av = resolveListSortScore(a);
  const bv = resolveListSortScore(b);
  const dir = sortOrder === "asc" ? 1 : -1;
  if (av == null && bv == null) {
    return String(a?.title || "").localeCompare(String(b?.title || ""));
  }
  if (av == null) return 1;
  if (bv == null) return -1;
  const diff = (Number(av) - Number(bv)) * dir;
  if (diff !== 0) return diff;
  return String(a?.title || "").localeCompare(String(b?.title || ""));
}

/** Highest displayable curator score first; unscored titles sink to the bottom. */
export function sortByDisplayCuratorScoreDesc(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => compareListSortScores(a, b, "desc"));
}

/** Sort by catalog percentile only (never raw score). Null percentiles sink last. */
export function sortByCuratorPercentileDesc(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const av = resolveCuratorPercentileValue(a);
    const bv = resolveCuratorPercentileValue(b);
    if (av == null && bv == null) {
      return String(a?.title || "").localeCompare(String(b?.title || ""));
    }
    if (av == null) return 1;
    if (bv == null) return -1;
    const diff = Number(bv) - Number(av);
    if (diff !== 0) return diff;
    return String(a?.title || "").localeCompare(String(b?.title || ""));
  });
}

function resolveRowYear(row) {
  const fromYear = Number(row?.year);
  if (Number.isFinite(fromYear) && fromYear > 0) return fromYear;
  const releaseDate = row?.releaseDate || row?.release_date || row?.first_air_date || "";
  const fromDate = Number(String(releaseDate).slice(0, 4));
  return Number.isFinite(fromDate) && fromDate > 0 ? fromDate : 0;
}

/** Newest release year first; within each year, highest curator score first. */
export function sortByYearThenDisplayCuratorScoreDesc(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const ay = resolveRowYear(a);
    const by = resolveRowYear(b);
    if (ay !== by) return by - ay;
    return compareListSortScores(a, b, "desc");
  });
}

/** Single-source score for list rows when Curator score is not shown (e.g. RT-only). */
export function formatAlternateSourceScoreLine(item) {
  if (isMovieMediaType(item) && !movieHasUserRatingSources(item)) return null;
  const ratings = ratingsFromItem(item);
  const rt = Number(ratings.rottentomatoes?.score);
  if (Number.isFinite(rt) && rt > 0) {
    return `RT ${Math.round(rt)}%`;
  }
  const mc = Number(ratings.metacritic?.score);
  if (Number.isFinite(mc) && mc > 0) {
    return `MC ${Math.round(mc)}%`;
  }
  const lb = Number(ratings.letterboxd?.score);
  if (Number.isFinite(lb) && lb > 0) {
    return `LB ${lb.toFixed(1)}/5`;
  }
  const imdb = Number(ratings.imdb?.score);
  if (Number.isFinite(imdb) && imdb > 0) {
    return `IMDb ${imdb.toFixed(1)}/10`;
  }
  return null;
}

/** Poster / search score line — hide Curator ★ 0% when only TMDB data exists.
 * Options:
 *   minRatings — override the default 10k ratings gate (use 0 for curated shelves)
 *   skipEligibility — show score even when movie source gates fail
 *   preferPercentile — force catalog percentile even for TV
 *   preferRawScore — legacy; TV already defaults to raw blend 1–100
 *     Movies always use catalog percentile.
 */
export function formatPosterScoreLine(item, options = {}) {
  const minRatings = Number.isFinite(Number(options.minRatings))
    ? Number(options.minRatings)
    : MIN_POSTER_SCORE_RATINGS;
  if (minRatings > 0 && !hasEnoughRatingsForScore(item, minRatings)) return null;
  const {
    compact = true,
    skipEligibility = false,
    preferPercentile = false,
  } = options;
  const fromTmdbOnly = item?.fromTmdbSearch === true || item?.inCuratorCatalog === false;
  const tmdbScore = Number(item?.tmdbScore ?? item?.vote_average);
  const ratingsOpts = { compact };
  if (fromTmdbOnly) {
    if (!Number.isFinite(tmdbScore) || tmdbScore <= 0) return null;
    const ratings = formatRatingsCount(item?.ratingsCount, ratingsOpts);
    return `TMDB ${tmdbScore.toFixed(1)}/10${ratings ? ` (${ratings})` : ""}`;
  }

  // TV: raw blend by default. Movies: catalog percentile only.
  const tvUsesRaw = isTvMediaType(item) && preferPercentile !== true;
  let score = null;
  if (tvUsesRaw) {
    score = resolveCuratorRawScore(item);
  } else if (skipEligibility) {
    score = resolveCuratorPercentileValue(item);
  } else {
    score = resolveCuratorPercentileDisplay(item);
  }
  if (hasValidCuratorScore(score)) {
    const ratings = formatRatingsCount(item?.ratingsCount, ratingsOpts);
    if (tvUsesRaw) {
      // Raw blend 1–100 — no % so it’s not read as a catalog percentile.
      if (compact) {
        return `★ ${Math.round(score)}${ratings ? ` (${ratings})` : ""}`;
      }
      return `★ ${Math.round(score)}/100${ratings ? ` (${ratings})` : ""}`;
    }
    return `★ ${Math.round(score)}%${ratings ? ` (${ratings})` : ""}`;
  }
  return null;
}

/** Theaters shelf: show estimated viewership instead of rating count when revenue exists. */
export function formatPosterScoreLineForTheaters(item, options = {}) {
  const admissions = resolveTheaterViewershipCount(item);
  if (!admissions) return formatPosterScoreLine(item, options);
  const { compact = true, preferPercentile = false } = options;
  const fromTmdbOnly = item?.fromTmdbSearch === true || item?.inCuratorCatalog === false;
  const tmdbScore = Number(item?.tmdbScore ?? item?.vote_average);
  const viewership = formatRatingsCount(admissions, { compact });
  if (fromTmdbOnly) {
    if (!Number.isFinite(tmdbScore) || tmdbScore <= 0) return null;
    return `TMDB ${tmdbScore.toFixed(1)}/10${viewership ? ` (${viewership})` : ""}`;
  }
  const useTvRaw = isTvMediaType(item) && preferPercentile !== true;
  const score = useTvRaw
    ? resolveCuratorRawScore(item)
    : resolveCuratorPercentileDisplay(item);
  if (hasValidCuratorScore(score) && hasEnoughRatingsForScore(item)) {
    if (useTvRaw) {
      return `★ ${Math.round(score)}${viewership ? ` (${viewership})` : ""}`;
    }
    return `★ ${Math.round(score)}%${viewership ? ` (${viewership})` : ""}`;
  }
  return null;
}

export function formatSearchScoreLine(item) {
  return formatPosterScoreLine(item) || "";
}

export function formatRuntime(minutes) {
  const value = Math.round(Number(minutes || 0));
  if (!Number.isFinite(value) || value <= 0) return null;
  const hours = Math.floor(value / 60);
  const mins = value % 60;
  if (!hours) return `${mins}m`;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

export function uniqueDisplayServiceWords(values, max = 3) {
  const labels = [];
  const seen = new Set();
  for (const source of filterDisplayWatchSources(values)) {
    const word = firstWord(source);
    if (!word || word === "Curator") continue;
    const key = word.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    labels.push(word);
    if (labels.length >= max) break;
  }
  return labels;
}

/** Short labels for tight hero/carousel meta lines. */
export function shortenGenreLabel(genre) {
  const raw = String(genre || "").trim();
  if (!raw) return "";
  if (/^science\s*fiction$/i.test(raw)) return "Sci Fi";
  if (/^sci[\s-]?fi$/i.test(raw)) return "Sci Fi";
  return raw;
}

export function formatGenreLabelsForDisplay(genres, { max = 2 } = {}) {
  const list = Array.isArray(genres) ? genres : [];
  return list
    .map((g) => shortenGenreLabel(g))
    .filter(Boolean)
    .slice(0, max);
}

/** Movie/TV · genres · certification (streaming shown via ServiceMark icon). */
export function buildHeroTypeGenreLine({
  mediaType = "movie",
  genres = [],
  genre = "",
  certification = null,
  maxGenres = 2,
  includeMediaType = true,
} = {}) {
  const typeLabel = includeMediaType
    ? (mediaType === "tv" || mediaType === "tv-show" ? "TV Show" : "Movie")
    : null;
  const genreList = Array.isArray(genres) && genres.length
    ? formatGenreLabelsForDisplay(genres, { max: maxGenres })
    : (genre ? formatGenreLabelsForDisplay([genre], { max: 1 }) : []);
  return [typeLabel, ...genreList, certification || null].filter(Boolean).join(" · ");
}

/** Year · runtime · ★ score + ratings count, or TMDB for external titles.
 * scoreAsRaw: format as ★ 71 (23K) instead of ★ 71% (23K). */
export function buildHeroFactsLine({
  year = null,
  yearLabel = null,
  runtime = null,
  score = null,
  ratingsCount = 0,
  tmdbScore = null,
  fromTmdbOnly = false,
  scoreAsRaw = false,
} = {}) {
  const yearPart = yearLabel || year || null;
  const runtimePart = formatRuntime(runtime);
  let scorePart = null;
  if (fromTmdbOnly) {
    const tmdb = Number(tmdbScore);
    if (Number.isFinite(tmdb) && tmdb > 0) {
      const ratings = formatRatingsCount(ratingsCount);
      scorePart = `TMDB ${tmdb.toFixed(1)}/10${ratings ? ` (${ratings})` : ""}`;
    }
  } else {
    const value = Number(score);
    if (Number.isFinite(value) && value > 0 && value <= 100) {
      const ratings = formatRatingsCount(ratingsCount, { compact: true });
      if (scoreAsRaw) {
        scorePart = `★ ${Math.round(value)}${ratings ? ` (${ratings})` : ""}`;
      } else {
        scorePart = `★ ${Math.round(value)}%${ratings ? ` (${ratings})` : ""}`;
      }
    }
  }
  return [yearPart, runtimePart, scorePart].filter(Boolean).join(" · ");
}

/** @deprecated Use buildHeroTypeGenreLine + buildHeroFactsLine instead. */
export function buildHeroCompactMetaLine(opts = {}) {
  const typeGenre = buildHeroTypeGenreLine(opts);
  const facts = buildHeroFactsLine(opts);
  return [typeGenre, facts].filter(Boolean).join(" · ");
}

const STREAMING_PROVIDER_ALIASES = {
  "netflix": ["netflix"],
  "apple tv plus": ["apple tv plus", "apple tv+"],
  "amazon prime video": ["amazon prime video", "prime video"],
  "hulu": ["hulu"],
  "disney plus": ["disney plus", "disney+"],
  "max": ["max", "hbo max"],
  "peacock": ["peacock", "peacock premium", "peacock premium plus"],
  "paramount plus": ["paramount plus", "paramount+", "paramount plus premium"],
};

/** True when cleaned watch sources include the requested library provider. */
export function movieMatchesStreamingProvider(watchSources, providerLabel) {
  const sources = filterDisplayWatchSources(watchSources).map((s) => s.toLowerCase());
  const key = String(providerLabel || "").trim().toLowerCase();
  const allowed = STREAMING_PROVIDER_ALIASES[key] || [key];
  return sources.some((source) => allowed.includes(source));
}

/** When a title has subscription streaming, prefer that over a stale in-theaters flag. */
export function effectiveInTheatersForDisplay(watchSources, inTheaters = false) {
  if (filterDisplayWatchSources(watchSources).length > 0) return false;
  return inTheaters === true;
}

export function getPreferredServiceLabel(watchSources, inTheaters = false) {
  const sources = filterDisplayWatchSources(watchSources);
  if (sources.length) {
    const lower = sources.map((s) => String(s).toLowerCase());
    for (const service of SERVICE_PRIORITY) {
      const needle = service.toLowerCase();
      const idx = lower.findIndex((s) => s.includes(needle));
      if (idx !== -1) return firstWord(sources[idx]);
    }
    return firstWord(sources[0]);
  }
  if (effectiveInTheatersForDisplay(watchSources, inTheaters)) return "In Theaters";
  return "Buy";
}

const LANGUAGE_LABELS = {
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  it: "Italian",
  ja: "Japanese",
  ko: "Korean",
  zh: "Chinese",
  pt: "Portuguese",
  hi: "Hindi",
  ru: "Russian",
};

function formatListReleaseLabel(item) {
  const releaseDate = item?.releaseDate;
  if (releaseDate) {
    const parsed = new Date(releaseDate);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString("en-US", { month: "short", year: "numeric" });
    }
  }
  if (item?.yearLabel) return item.yearLabel;
  if (item?.year) return String(item.year);
  return null;
}

/** Parts for list row subtitle: release date + media type. */
export function formatListRowYearParts(item) {
  const isTv = item?.mediaType === "tv" || item?.mediaType === "tv-show";
  const typeLabel = isTv ? "TV Series" : "Movie";
  const releaseLabel = formatListReleaseLabel(item);
  return { releaseLabel, typeLabel };
}

/** "Jan 2026 · Movie" or "2019–2026 · TV Series" */
export function formatListRowYearLine(item) {
  const { releaseLabel, typeLabel } = formatListRowYearParts(item);
  return releaseLabel ? `${releaseLabel} · ${typeLabel}` : typeLabel;
}

/** "#11 Most popular on Letterboxd" or "#3 on IMDb TVmeter" when rank data exists. */
export function formatListRowPopularityLine(item) {
  const rank = Number(item?.popularityRank);
  if (!Number.isFinite(rank) || rank <= 0) return null;
  const isTv = item?.mediaType === "tv" || item?.mediaType === "tv-show";
  if (isTv) return `#${rank} on IMDb TVmeter`;
  return `#${rank} Most popular on Letterboxd`;
}

/** "Horror & Thriller · 1h 53 min" */
export function formatListRowGenreRuntimeLine(item) {
  const genres = formatGenreLabelsForDisplay(
    (Array.isArray(item?.genreList) && item.genreList.length
      ? item.genreList
      : (Array.isArray(item?.genres) ? item.genres : [])),
    { max: 2 },
  );
  const runtimePart = formatRuntime(item?.runtime);
  const parts = [];
  if (genres.length) parts.push(genres.join(" & "));
  if (runtimePart) parts.push(runtimePart);
  return parts.length ? parts.join(" · ") : null;
}

/** "English · United States" when language is known. */
export function formatListRowLanguageLine(item) {
  const code = String(item?.originalLanguage || "").trim().toLowerCase();
  if (!code) return null;
  const language = LANGUAGE_LABELS[code] || code.toUpperCase();
  return language;
}

/** True when a list row may show or rank by a Curator score (10k+ ratings + source coverage). */
export function hasDisplayableListRowScore(item) {
  if (!hasEnoughRatingsForScore(item)) return false;
  const fromTmdbOnly = item?.fromTmdbSearch === true || item?.inCuratorCatalog === false;
  if (fromTmdbOnly) {
    const tmdbScore = Number(item?.tmdbScore ?? item?.vote_average);
    return Number.isFinite(tmdbScore) && tmdbScore > 0;
  }
  if (!movieHasDisplayableCuratorScore(item)) return false;
  if (isTvMediaType(item)) {
    return hasValidCuratorScore(resolveCuratorRawScore(item));
  }
  return hasValidCuratorScore(resolveCuratorPercentileDisplay(item));
}

/** Score + ratings for list rows: "★ 45% (1.1M)" — curator only, 10k+ ratings.
 *  TV shows use raw blend (★ 73) instead of catalog percentile. */
export function formatListRowScoreText(item) {
  if (hasDisplayableListRowScore(item)) {
    const fromTmdbOnly = item?.fromTmdbSearch === true || item?.inCuratorCatalog === false;
    const tmdbScore = Number(item?.tmdbScore ?? item?.vote_average);
    const ratings = formatRatingsCount(item?.ratingsCount);
    if (fromTmdbOnly) {
      return `TMDB ${tmdbScore.toFixed(1)}/10${ratings ? ` (${ratings})` : ""}`;
    }
    if (isTvMediaType(item)) {
      const raw = resolveCuratorRawScore(item);
      if (!hasValidCuratorScore(raw)) return null;
      return `★ ${Math.round(raw)}${ratings ? ` (${ratings})` : ""}`;
    }
    const score = resolveCuratorPercentileDisplay(item);
    return `★ ${Math.round(score)}%${ratings ? ` (${ratings})` : ""}`;
  }
  const alternate = formatAlternateSourceScoreLine(item);
  if (alternate) return alternate;
  const fromTmdbOnly = item?.fromTmdbSearch === true || item?.inCuratorCatalog === false;
  const tmdbScore = Number(item?.tmdbScore ?? item?.vote_average);
  if (fromTmdbOnly && Number.isFinite(tmdbScore) && tmdbScore > 0) {
    const ratings = formatRatingsCount(item?.ratingsCount);
    return `TMDB ${tmdbScore.toFixed(1)}/10${ratings ? ` (${ratings})` : ""}`;
  }
  return null;
}

/** Compact score for detail-style dock line: "★ 99%" (no ratings count).
 *  TV shows use raw blend without %. */
export function formatListRowScorePercentOnly(item) {
  if (hasDisplayableListRowScore(item)) {
    const fromTmdbOnly = item?.fromTmdbSearch === true || item?.inCuratorCatalog === false;
    const tmdbScore = Number(item?.tmdbScore ?? item?.vote_average);
    if (fromTmdbOnly) {
      return Number.isFinite(tmdbScore) && tmdbScore > 0 ? `TMDB ${tmdbScore.toFixed(1)}` : null;
    }
    if (isTvMediaType(item)) {
      const raw = resolveCuratorRawScore(item);
      return hasValidCuratorScore(raw) ? `★ ${Math.round(raw)}` : null;
    }
    const score = resolveCuratorPercentileDisplay(item);
    return hasValidCuratorScore(score) ? `★ ${Math.round(score)}%` : null;
  }
  const full = formatListRowScoreText(item);
  if (!full) return null;
  return full.replace(/\s*\([^)]*\)\s*$/, "").trim() || full;
}

/** ISO language code for compact meta (en → EN). */
export function formatListLanguageCode(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const match = raw.match(/^([a-z]{2,3})/i);
  return match ? match[1].toUpperCase() : null;
}

/**
 * Detail dock line under title:
 * "★ 99% (4M) Adventure & Fantasy · Max"
 */
export function formatListRowDetailMetaLine(item, scoreOverride = undefined) {
  let scorePart = scoreOverride === undefined
    ? formatListRowScoreText(item)
    : (scoreOverride || null);
  // Custom score overrides (e.g. theaters) may omit count — append when missing.
  if (scorePart && scoreOverride !== undefined && !/\(\s*[\d.]+[KMB]?\s*\)/i.test(scorePart)) {
    const ratings = formatRatingsCount(item?.ratingsCount);
    if (ratings) scorePart = `${scorePart} (${ratings})`;
  }
  const genres = formatGenreLabelsForDisplay(
    (Array.isArray(item?.genreList) && item.genreList.length
      ? item.genreList
      : (Array.isArray(item?.genres) ? item.genres : [])),
    { max: 2 },
  );
  const genrePart = genres.length ? genres.join(" & ") : null;
  // Match detail dock: only surface a service when streaming/theaters exist.
  const hasStreaming = filterDisplayWatchSources(item?.watchSources).length > 0;
  const inTheaters = effectiveInTheatersForDisplay(item?.watchSources, item?.inTheaters);
  const servicePart = (hasStreaming || inTheaters)
    ? getPreferredServiceLabel(item?.watchSources, item?.inTheaters)
    : null;
  const head = [scorePart, genrePart].filter(Boolean).join(" ");
  if (head && servicePart) return `${head} · ${servicePart}`;
  return head || servicePart || null;
}

/**
 * Detail about/spec line:
 * "Movie · 2003 · 3h 21m · 11+ · EN"
 * (Ratings count lives next to the score on the meta line above.)
 */
export function formatListRowDetailSpecLine(item) {
  const isTv = item?.mediaType === "tv" || item?.mediaType === "tv-show";
  const typePart = isTv ? "TV" : "Movie";
  const yearPart = item?.yearLabel
    || (item?.year ? String(item.year) : null)
    || (item?.releaseDate ? String(item.releaseDate).slice(0, 4) : null);
  const runtimePart = formatRuntime(item?.runtime);
  const agePart = String(item?.commonSenseAge || item?.certification || "").trim() || null;
  const languagePart = formatListLanguageCode(item?.originalLanguage);
  const parts = [typePart, yearPart, runtimePart, agePart, languagePart].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

// Returns a branded TMDB logo URL for a single provider name, or null when we
// have no logo for that provider (caller can fall back to the text label).
export function getServiceIconUrl(serviceName) {
  const value = String(serviceName || "").trim();
  if (!value) return null;
  const match = PROVIDER_LOGOS.find((entry) => entry.match.test(value));
  if (!match) return null;
  // Some providers use a locally-hosted logo (e.g. the official HBO Max icon)
  // instead of the TMDB CDN path.
  if (match.url) return match.url;
  return `${TMDB_IMAGE_BASE}${match.path}`;
}

/**
 * Classify a watch provider as subscription streaming vs free/AVOD/library.
 * Prefer TMDB monetization hints (`subscription` | `free` | `ads` | `flatrate`).
 * Fallback: known free/library/ad-supported name patterns.
 * Note: live-TV apps like fuboTV / Philo are paid → "streaming", not free.
 */
const FREE_WATCH_PROVIDER_RE = /\b(tubi|pluto|freevee|kanopy|hoopla|crackle|plex|roku\s*channel|free\s*with\s*ads|with\s*ads|ad[-\s]?supported)\b/i;

export function classifyWatchServiceMonetization(serviceName, monetizationHint = null) {
  const hint = String(monetizationHint || "").trim().toLowerCase();
  if (hint === "free" || hint === "ads") return "free";
  if (hint === "subscription" || hint === "flatrate" || hint === "streaming") return "streaming";
  const value = String(serviceName || "").trim();
  if (!value) return "streaming";
  if (FREE_WATCH_PROVIDER_RE.test(value)) return "free";
  return "streaming";
}

export function watchServiceNoteForKind(kind) {
  if (kind === "free") return "Free";
  if (kind === "theaters") return null;
  if (kind === "buy") return null;
  return "Streaming now";
}

/** First letter of a provider label for badge fallbacks when no logo exists. */
export function getServiceInitial(label) {
  const cleaned = String(label || "").trim();
  if (!cleaned) return "?";
  return cleaned.charAt(0).toUpperCase();
}

// Returns { label, iconUrl, kind } for the highest-priority streamable provider
// in a list. `kind` is one of "streaming" | "theaters" | "buy" so callers can
// substitute the popcorn (theaters) or shopping-bag (buy) glyph when there is
// no streaming logo. iconUrl is null for theatrical/buy-only titles or when the
// provider has no known logo, so cards can gracefully fall back.
export function getPreferredServiceIcon(watchSources, inTheaters = false) {
  const sources = filterDisplayWatchSources(watchSources);
  if (sources.length) {
    const label = getPreferredServiceLabel(sources, false);
    const lower = sources.map((s) => String(s).toLowerCase());
    for (const service of SERVICE_PRIORITY) {
      const needle = service.toLowerCase();
      const idx = lower.findIndex((s) => s.includes(needle));
      if (idx !== -1) {
        const iconUrl = getServiceIconUrl(sources[idx]);
        if (iconUrl) return { label, iconUrl, kind: "streaming" };
      }
    }
    for (const source of sources) {
      const iconUrl = getServiceIconUrl(source);
      if (iconUrl) return { label, iconUrl, kind: "streaming" };
    }
    return { label, iconUrl: null, kind: "streaming" };
  }

  if (effectiveInTheatersForDisplay(watchSources, inTheaters)) {
    return { label: "In Theaters", iconUrl: null, kind: "theaters" };
  }

  const label = getPreferredServiceLabel(sources, false);
  return { label, iconUrl: null, kind: "buy" };
}

/** Split copy into two lines with roughly equal character counts (word boundaries). */
export function splitBalancedTwoLines(text) {
  const clean = String(text || "").trim().replace(/\s+/g, " ");
  if (!clean) return null;

  const words = clean.split(" ").filter(Boolean);
  if (words.length < 2 || clean.length < 20) {
    return { line1: clean, line2: "" };
  }

  const target = clean.length / 2;
  let bestSplit = 1;
  let bestScore = Infinity;

  for (let i = 1; i < words.length; i += 1) {
    const line1 = words.slice(0, i).join(" ");
    const line2 = words.slice(i).join(" ");
    const charDiff = Math.abs(line1.length - line2.length);
    const midpointPenalty = Math.abs(line1.length - target) * 0.35;
    const shortLinePenalty = Math.min(line1.length, line2.length) < 10 ? 6 : 0;
    const score = charDiff + midpointPenalty + shortLinePenalty;
    if (score < bestScore) {
      bestScore = score;
      bestSplit = i;
    }
  }

  return {
    line1: words.slice(0, bestSplit).join(" "),
    line2: words.slice(bestSplit).join(" "),
  };
}

/** First + last name initials for person avatar placeholders (Apple Contacts style). */
export function formatPersonInitials(name) {
  const cleaned = String(name || "").trim();
  if (!cleaned) return "?";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
}
